import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { createMemoryStore } from './memoryStore'
import type { OutboxItem, OutboxStore } from './types'

/**
 * IndexedDB via `idb` — offline-sync.md §3.
 *
 * *"Why IndexedDB, not localStorage: it survives more, holds more, and is async
 * — a synchronous localStorage write on the frame loop is a frame drop."*
 *
 * Two stores. `outbox` is the queue; `failed` is where an item goes after
 * burning its attempts, because deleting it would hide the failure from the
 * quiet indicator §4.7 shows the teacher.
 */

const DB_NAME = 'kalqy'
const DB_VERSION = 1

interface OutboxDB extends DBSchema {
  outbox: { key: number; value: OutboxItem }
  failed: { key: number; value: OutboxItem }
}

let dbPromise: Promise<IDBPDatabase<OutboxDB>> | null = null

function db(): Promise<IDBPDatabase<OutboxDB>> {
  dbPromise ??= openDB<OutboxDB>(DB_NAME, DB_VERSION, {
    upgrade(database) {
      // autoIncrement: FIFO order IS insertion order, which is the property the
      // whole retry policy rests on (§5: "strict FIFO — progress writes must not
      // overtake their events").
      database.createObjectStore('outbox', { keyPath: 'id', autoIncrement: true })
      database.createObjectStore('failed', { keyPath: 'id', autoIncrement: true })
    },
  })
  return dbPromise
}

function createIdbStore(): OutboxStore {
  return {
    async add(item) {
      const { id: _dropped, ...rest } = item
      return (await db()).add('outbox', rest as OutboxItem)
    },
    async head(limit) {
      // getAll respects key order, and the key is the autoIncrement id.
      return (await db()).getAll('outbox', undefined, limit)
    },
    async update(item) {
      if (item.id === undefined) return
      /*
       * 🔴 ONLY IF IT IS STILL QUEUED. `put` is an upsert, so a bare one would
       * re-create an item the size cap dropped while a flush was holding it —
       * the memory store has always ignored a missing id, and the two must not
       * drift on what an update means. One transaction, so the check and the
       * write cannot be split by a removal in between.
       */
      const tx = (await db()).transaction('outbox', 'readwrite')
      if ((await tx.store.getKey(item.id)) !== undefined) await tx.store.put(item)
      await tx.done
    },
    async remove(id) {
      await (await db()).delete('outbox', id)
    },
    async removeMany(ids) {
      // One transaction for a whole batch: a hundred deletes, one commit.
      const tx = (await db()).transaction('outbox', 'readwrite')
      await Promise.all([...ids.map((id) => tx.store.delete(id)), tx.done])
    },
    count() {
      return db().then((d) => d.count('outbox'))
    },
    all() {
      return db().then((d) => d.getAll('outbox'))
    },
    async fail(item) {
      const database = await db()
      const { id, ...rest } = item
      await database.add('failed', rest as OutboxItem)
      if (id !== undefined) await database.delete('outbox', id)
    },
    failed() {
      return db().then((d) => d.getAll('failed'))
    },
    async restoreFailed() {
      const database = await db()
      const items = await database.getAll('failed')
      /*
       * One transaction over both stores, so a retry cannot half-apply: an
       * interrupted move that had already deleted from `failed` would lose the
       * items outright, which is the one outcome worse than leaving them stuck.
       */
      const tx = database.transaction(['outbox', 'failed'], 'readwrite')
      for (const item of items) {
        const { id: _dropped, ...rest } = item
        await tx.objectStore('outbox').add({ ...rest, attempts: 0 } as OutboxItem)
      }
      await tx.objectStore('failed').clear()
      await tx.done
      return items.length
    },
  }
}

/**
 * IndexedDB when it exists, memory when it does not.
 *
 * 🔴 A DENIED STORAGE MUST NOT STOP A CHILD PLAYING. Safari private mode and
 * locked-down school profiles both take IndexedDB away; falling back to memory
 * costs this device's queue on reload and keeps the session working. Same
 * reasoning as the device store's null-storage path (§4.5).
 */
export function createOutboxStore(): OutboxStore {
  try {
    if (typeof indexedDB === 'undefined') return createMemoryStore()
    return createIdbStore()
  } catch {
    return createMemoryStore()
  }
}
